// @ts-nocheck
/**
 * Template V2 font catalog (DEC-064 / WP-063).
 *
 * Templates persist logical `TemplateFontId` values on `StyleBlock.fontFamily`.
 * Platform adapters resolve ids to RN registration names / CSS families via
 * this registry. Never persist PostScript or CSS font-family strings in JSON.
 *
 * Pure TypeScript — no React Native imports (DEC-016).
 */

/** Closed catalog of bundlable template typefaces. */
export type TemplateFontId =
  | 'quicksand'
  | 'system'
  | 'spaceMono'
  | 'nunitoSans'
  | 'sourceSerif4';

/** Default when StyleBlock.fontFamily is omitted or unknown. */
export const DEFAULT_TEMPLATE_FONT_ID: TemplateFontId = 'nunitoSans';

export const TEMPLATE_FONT_IDS: readonly TemplateFontId[] = [
  'quicksand',
  'system',
  'spaceMono',
  'nunitoSans',
  'sourceSerif4',
] as const;

export interface TemplateFontFace {
  id: TemplateFontId;
  /** Authoring / inspector label (English source; editors may i18n). */
  label: string;
  /** SPDX-style or short license note for bundled assets. */
  license: string;
  /**
   * expo-font / RN `fontFamily` registration key.
   * Undefined for `system` (platform default).
   */
  rnFontFamily?: string;
  /**
   * CSS `font-family` name for web / editor preview.
   * Must match `@font-face` / local asset registration.
   */
  webFontFamily: string;
  /** Filename under Containd `assets/fonts` and editor `public/fonts`. */
  assetFileName?: string;
}

/**
 * Single source of truth for catalog metadata and platform name mapping.
 * Grow only by shipping a new asset + registry row.
 */
export const TEMPLATE_FONT_CATALOG: Record<TemplateFontId, TemplateFontFace> = {
  quicksand: {
    id: 'quicksand',
    label: 'Quicksand',
    license: 'OFL-1.1',
    rnFontFamily: 'Quicksand-VariableFont_wght',
    webFontFamily: 'Quicksand',
    assetFileName: 'Quicksand-VariableFont_wght.ttf',
  },
  system: {
    id: 'system',
    label: 'System',
    license: 'n/a',
    webFontFamily: 'system-ui, sans-serif',
  },
  spaceMono: {
    id: 'spaceMono',
    label: 'Space Mono',
    license: 'OFL-1.1',
    rnFontFamily: 'SpaceMono-Regular',
    webFontFamily: 'Space Mono',
    assetFileName: 'SpaceMono-Regular.ttf',
  },
  nunitoSans: {
    id: 'nunitoSans',
    label: 'Nunito Sans',
    license: 'OFL-1.1',
    /** Registration key must match expo-font `useFonts` map key. */
    rnFontFamily: 'NunitoSans-Variable',
    webFontFamily: 'Nunito Sans',
    assetFileName: 'NunitoSans-Variable.ttf',
  },
  sourceSerif4: {
    id: 'sourceSerif4',
    label: 'Source Serif 4',
    license: 'OFL-1.1',
    rnFontFamily: 'SourceSerif4-Variable',
    webFontFamily: 'Source Serif 4',
    assetFileName: 'SourceSerif4-Variable.ttf',
  },
};

export function isTemplateFontId(value: unknown): value is TemplateFontId {
  return typeof value === 'string' && (TEMPLATE_FONT_IDS as readonly string[]).includes(value);
}

/** Coerce unknown / omitted values to the default catalog id. */
export function normalizeTemplateFontId(value: unknown): TemplateFontId {
  return isTemplateFontId(value) ? value : DEFAULT_TEMPLATE_FONT_ID;
}

export type TemplateFontPlatform = 'ios' | 'android' | 'web' | 'native' | string;

/**
 * Resolve a catalog id to the platform `fontFamily` string for React Native
 * (iOS, Android, and RN-web). Bundled faces use the expo-font registration key
 * (`Quicksand-VariableFont_wght`, etc.) — the same string Containd UI already uses.
 *
 * Returns `undefined` for `system` on native/RN-web (platform default).
 * For HTML/CSS surfaces (template editor preview), use `resolveTemplateFontCssFamily`.
 */
export function resolveTemplateFontFamily(
  id: TemplateFontId | undefined,
  platform: TemplateFontPlatform,
): string | undefined {
  const face = TEMPLATE_FONT_CATALOG[normalizeTemplateFontId(id)];
  if (face.id === 'system') {
    // RN-web still prefers undefined so Tamagui/RN pick the UA default;
    // pure CSS stacks are handled by resolveTemplateFontCssFamily.
    if (platform === 'web') {
      return undefined;
    }
    return undefined;
  }
  return face.rnFontFamily;
}

/**
 * Resolve a catalog id to a CSS `font-family` for HTML preview / `@font-face`
 * (template editor). Names must match editor `public/fonts` face declarations.
 */
export function resolveTemplateFontCssFamily(id: TemplateFontId | undefined): string {
  const face = TEMPLATE_FONT_CATALOG[normalizeTemplateFontId(id)];
  return face.webFontFamily;
}
